import { quarantineOutboxForEvictedCases } from "../domain/applyEviction";
import type { DueAnchor } from "../domain/dueWithAnchors";
import type {
  BundleCase,
  CareBundle,
  ConflictRecord,
  LocalCareEntry,
  MediaQueueItem,
  NoticeRecord,
  OfflineStore,
} from "./types";
import type { OutboxItem } from "../domain/outboxScheduler";

type State = {
  outbox: OutboxItem[];
  bundle: CareBundle;
  localCases: BundleCase[];
  localEntries: LocalCareEntry[];
  media: MediaQueueItem[];
  conflicts: ConflictRecord[];
  notices: NoticeRecord[];
  anchors: DueAnchor[];
};

function emptyBundle(): CareBundle {
  return {
    cases: [],
    sites: [],
    place_nodes: [],
    entries_tail: [],
    due_anchors: [],
    evicted_case_ids: [],
    site_ids: [],
  };
}

function emptyState(): State {
  return {
    outbox: [],
    bundle: emptyBundle(),
    localCases: [],
    localEntries: [],
    media: [],
    conflicts: [],
    notices: [],
    anchors: [],
  };
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export function createMemoryStore(initial?: Partial<State>): OfflineStore {
  let state: State = { ...emptyState(), ...initial };

  const store: OfflineStore = {
    async getOutbox() {
      return clone(state.outbox);
    },
    async upsertOutbox(item) {
      const idx = state.outbox.findIndex((row) => row.id === item.id);
      if (idx >= 0) state.outbox[idx] = clone(item);
      else state.outbox.push(clone(item));
    },
    async removeOutbox(id) {
      state.outbox = state.outbox.filter((row) => row.id !== id);
    },
    async getBundle() {
      return clone(state.bundle);
    },
    async setBundle(bundle) {
      state.bundle = clone(bundle);
      if (Array.isArray(bundle.cases)) {
        const byId = new Map(state.localCases.map((row) => [row.id, row]));
        for (const row of bundle.cases) {
          const prev = byId.get(row.id);
          byId.set(row.id, { ...prev, ...row, pendingSync: prev?.pendingSync ?? false });
        }
        state.localCases = [...byId.values()];
      }
      if (Array.isArray(bundle.entries_tail)) {
        const pending = state.localEntries.filter((e) => e.syncStatus === "pending");
        const remote = bundle.entries_tail.map((e) => ({
          ...e,
          events: e.events ?? [],
          syncStatus: "synced" as const,
        }));
        const pendingIds = new Set(pending.map((e) => e.client_entry_id));
        state.localEntries = [
          ...remote.filter((e) => !pendingIds.has(e.client_entry_id)),
          ...pending,
        ];
      }
      if (Array.isArray(bundle.due_anchors)) {
        state.anchors = clone(bundle.due_anchors);
      }
    },
    async getKnownCaseIds() {
      const ids = new Set<string>();
      for (const row of state.localCases) ids.add(row.id);
      for (const row of state.bundle.cases ?? []) ids.add(row.id);
      return [...ids];
    },
    async upsertLocalCase(row) {
      const idx = state.localCases.findIndex((c) => c.id === row.id);
      if (idx >= 0) state.localCases[idx] = { ...state.localCases[idx], ...clone(row) };
      else state.localCases.push(clone(row));
    },
    async getLocalCase(id) {
      return clone(state.localCases.find((c) => c.id === id) ?? null);
    },
    async listLocalCases() {
      return clone(state.localCases);
    },
    async appendLocalEntry(entry) {
      const idx = state.localEntries.findIndex(
        (e) => e.client_entry_id === entry.client_entry_id
      );
      if (idx >= 0) state.localEntries[idx] = clone(entry);
      else state.localEntries.push(clone(entry));
    },
    async getLocalEntries(caseId) {
      return clone(
        state.localEntries
          .filter((e) => e.case_id === caseId)
          .sort((a, b) => b.recorded_at.localeCompare(a.recorded_at))
      );
    },
    async getDueAnchors() {
      return clone(state.anchors);
    },
    async setDueAnchors(anchors) {
      state.anchors = clone(anchors);
    },
    async getMediaQueue() {
      return clone(state.media);
    },
    async upsertMedia(item) {
      const idx = state.media.findIndex((m) => m.id === item.id);
      if (idx >= 0) state.media[idx] = clone(item);
      else state.media.push(clone(item));
    },
    async getConflicts() {
      return clone(state.conflicts);
    },
    async upsertConflict(conflict) {
      const idx = state.conflicts.findIndex((c) => c.id === conflict.id);
      if (idx >= 0) state.conflicts[idx] = clone(conflict);
      else state.conflicts.push(clone(conflict));
    },
    async deleteConflict(id) {
      state.conflicts = state.conflicts.filter((c) => c.id !== id);
    },
    async getNotices() {
      return clone(state.notices);
    },
    async addNotice(body) {
      state.notices.push({
        id: `n_${state.notices.length + 1}_${Date.now()}`,
        body,
        createdAt: new Date().toISOString(),
        read: false,
      });
    },
    async upsertKeyedNotice(key, body, seenAt) {
      const idx = state.notices.findIndex((row) => row.key === key);
      if (idx >= 0) {
        state.notices[idx] = { ...state.notices[idx], body, lastSeenAt: seenAt };
        return clone(state.notices[idx]);
      }
      const created: NoticeRecord = {
        id: `n_${state.notices.length + 1}_${Date.now()}`,
        key,
        body,
        createdAt: seenAt,
        lastSeenAt: seenAt,
        read: false,
      };
      state.notices.push(created);
      return clone(created);
    },
    async removeNoticesByKeyPrefix(prefix) {
      state.notices = state.notices.filter((row) => !row.key?.startsWith(prefix));
    },
    async markNoticeRead(id) {
      const idx = state.notices.findIndex((row) => row.id === id);
      if (idx < 0) return;
      state.notices[idx] = { ...state.notices[idx], read: true };
    },
    async remapCaseId(fromId, toId) {
      if (fromId === toId) return;
      state.localCases = state.localCases.map((row) =>
        row.id === fromId ? { ...row, id: toId, pendingSync: false } : row
      );
      state.localEntries = state.localEntries.map((row) =>
        row.case_id === fromId ? { ...row, case_id: toId } : row
      );
      state.outbox = state.outbox.map((row) => ({
        ...row,
        caseId: row.caseId === fromId ? toId : row.caseId,
        dependsOn: row.dependsOn.map((dep) => (dep === fromId ? toId : dep)),
        payload:
          row.payload.caseId === fromId
            ? { ...row.payload, caseId: toId }
            : row.payload,
      }));
      state.anchors = state.anchors.map((row) =>
        row.case_id === fromId ? { ...row, case_id: toId } : row
      );
    },
    async evictCases(ids, notice) {
      const evicted = new Set(ids);
      state.localCases = state.localCases.filter((c) => !evicted.has(c.id));
      state.localEntries = state.localEntries.filter((e) => !evicted.has(e.case_id));
      state.anchors = state.anchors.filter((a) => !evicted.has(a.case_id));
      state.bundle = {
        ...state.bundle,
        cases: (state.bundle.cases ?? []).filter((c) => !evicted.has(c.id)),
        entries_tail: (state.bundle.entries_tail ?? []).filter(
          (e) => !evicted.has(e.case_id)
        ),
      };
      state.outbox = quarantineOutboxForEvictedCases(state.outbox, ids, notice);
      if (ids.length) await store.addNotice(notice);
    },
    async purgeSitesNotIn(siteIds) {
      const keep = new Set(siteIds);
      const dropCases = state.localCases
        .filter((c) => c.current_care_site_id && !keep.has(c.current_care_site_id))
        .map((c) => c.id);
      if (dropCases.length) {
        await store.evictCases(
          dropCases,
          "Zugriff auf Pflegestelle entzogen. Ungesendete Einträge sind in Quarantäne."
        );
      }
      state.bundle = {
        ...state.bundle,
        sites: (state.bundle.sites ?? []).filter((s) => keep.has(String(s.id))),
        place_nodes: (state.bundle.place_nodes ?? []).filter((n) =>
          keep.has(String(n.care_site_id))
        ),
        site_ids: siteIds,
      };
    },
    async clearAll() {
      state = emptyState();
    },
    async snapshot() {
      return JSON.stringify(state);
    },
    async hydrate(raw) {
      state = JSON.parse(raw) as State;
    },
  };

  return store;
}
